<template>
  <div>
    <v-row class="mb-4 pa-2">
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <v-card v-else-if="selectedTeam[0] === 'Un-Assigned'">
          <v-card-text>
            <div class="text-h6">{{ $t('cards.unassigned') }}</div>
            <div class="text-h4">{{ unassignedStats.percentage.toFixed(1) }}%</div>
            <v-progress-linear :model-value="unassignedStats.percentage"></v-progress-linear>
            <div class="text-body-2 mt-2">
              {{ unassignedStats.count }} / {{ unassignedStats.total }}
            </div>
          </v-card-text>
        </v-card>
        <v-card v-else>
          <v-card-text>
            <div class="text-h6">{{ $t('cards.userUnassigned') }}</div>
            <div class="text-h4">{{ userUnassignedStats.percentage.toFixed(1) }}%</div>
            <v-progress-linear :model-value="userUnassignedStats.percentage"></v-progress-linear>
            <div class="text-body-2 mt-2">
              {{ userUnassignedStats.count }} / {{ teamStats.filteredCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <v-card v-else>
          <v-card-text>
            <div class="text-h6">{{ $t('cards.draft') }}</div>
            <div class="text-h4">{{ draftStats.percentage.toFixed(1) }}%</div>
            <v-progress-linear :model-value="draftStats.percentage"></v-progress-linear>
            <div class="text-body-2 mt-2">
              {{ draftStats.count }} / {{ selectedTeam[0] === 'Un-Assigned' ? draftStats.total : teamStats.filteredCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <v-card v-else>
          <v-card-text>
            <div class="text-h6">{{ $t('cards.open') }}</div>
            <div class="text-h4">{{ openStats.percentage.toFixed(1) }}%</div>
            <v-progress-linear :model-value="openStats.percentage"></v-progress-linear>
            <div class="text-body-2 mt-2">
              {{ openStats.count }} / {{ selectedTeam[0] === 'Un-Assigned' ? openStats.total : teamStats.filteredCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <v-card v-else>
          <v-card-text>
            <div class="text-h6">{{ $t('cards.closed') }}</div>
            <div class="text-h4">{{ closedStats.percentage.toFixed(1) }}%</div>
            <v-progress-linear :model-value="closedStats.percentage"></v-progress-linear>
            <div class="text-body-2 mt-2">
              {{ closedStats.count }} / {{ selectedTeam[0] === 'Un-Assigned' ? closedStats.total : teamStats.filteredCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <TeamsUsersBarChart
          @chartClick="handleChartClick"
          :selectedTeam="selectedTeam"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
          @updateSelectedTeam="updateSelectedTeam"
        />
      </v-col>
      <v-col cols="12" md="6">
        <CategoryStackedBarChart
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"                   
        />
        <TeamsDrawingCategoryStackedBarChart
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"                     
        />                       
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12">
        <ItemsGrid
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import ItemsGrid from './grids/ItemsGrid.vue';
import CategoryStackedBarChart from './charts/CategoryStackedBarChart.vue';
import TeamsDrawingCategoryStackedBarChart from './charts/DrawingDrawingCategoryStackedBarChart.vue';
import TeamsUsersBarChart from './charts/TeamsUsersBarChart.vue';

const store = useStore();
const dataLoaded = computed(() => store.getters.getDataLoaded);
const conn = store.getters.getDBConnection;
const filterContext = computed(() => store.state.filterItemsContext);
const selectedTeam = ref(['Un-Assigned']);

// Stats refs
const unassignedStats = ref({ count: 0, total: 0, percentage: 0 });
const userUnassignedStats = ref({ count: 0, total: 0, percentage: 0 });
const draftStats = ref({ count: 0, total: 0, percentage: 0 });
const openStats = ref({ count: 0, total: 0, percentage: 0 });
const closedStats = ref({ count: 0, total: 0, percentage: 0 });
const teamStats = ref({ filteredCount: 0 });

const updateSelectedTeam = (teamId) => {
  selectedTeam.value = teamId;
  computeStats();
};

const handleChartClick = () => {
  // Implement your chart click handler
};

const computeStats = async () => {
  if (!dataLoaded.value) return;
  
  const isUnassigned = selectedTeam.value[0] === 'Un-Assigned';
  
  try {
    // Query stats based on whether 'Un-Assigned' is selected or a specific team
    if (isUnassigned) {
      // Stats for unassigned view (no team selected)
      const query = `
        WITH 
        total_items AS (
          SELECT COUNT(*) AS total FROM filteredItems
        ),
        unassigned_count AS (
          SELECT COUNT(*) AS count FROM filteredItems WHERE AssigneeTeamId = ''
        ),
        status_counts AS (
          SELECT 
            COUNT(CASE WHEN st.IsDraft = TRUE THEN 1 END) AS draft_count,
            COUNT(CASE WHEN st.IsTerminal = TRUE THEN 1 END) AS closed_count,
            COUNT(CASE WHEN st.IsDraft = FALSE AND st.IsTerminal = FALSE THEN 1 END) AS open_count
          FROM filteredItems fi
          JOIN status_templates st ON fi.StatusId = st.Id
        )
        SELECT 
          t.total,
          uc.count AS unassigned_count,
          sc.draft_count,
          sc.closed_count,
          sc.open_count
        FROM 
          total_items t, 
          unassigned_count uc,
          status_counts sc
      `;
      
      const result = await conn.query(query);
      const stats = result.toArray()[0].toJSON();
      
      const total = Number(stats.total) || 0;
      const unassignedCount = Number(stats.unassigned_count) || 0;
      const draftCount = Number(stats.draft_count) || 0;
      const closedCount = Number(stats.closed_count) || 0;
      const openCount = Number(stats.open_count) || 0;
      
      unassignedStats.value = {
        count: unassignedCount,
        total: total,
        percentage: total > 0 ? (unassignedCount / total) * 100 : 0
      };
      
      draftStats.value = {
        count: draftCount,
        total: total,
        percentage: total > 0 ? (draftCount / total) * 100 : 0
      };
      
      openStats.value = {
        count: openCount,
        total: total,
        percentage: total > 0 ? (openCount / total) * 100 : 0
      };
      
      closedStats.value = {
        count: closedCount,
        total: total,
        percentage: total > 0 ? (closedCount / total) * 100 : 0
      };
      
      // Clear user unassigned stats in unassigned view
      userUnassignedStats.value = { count: 0, total: 0, percentage: 0 };
      
    } else {
      // Stats for a specific team
      const teamId = selectedTeam.value[0];
      const query = `
        WITH 
        team_items AS (
          SELECT COUNT(*) AS total FROM filteredItems WHERE AssigneeTeamId = '${teamId}'
        ),
        team_user_unassigned AS (
          SELECT COUNT(*) AS count 
          FROM filteredItems 
          WHERE AssigneeTeamId = '${teamId}' AND (AssigneeUserIds IS NULL OR AssigneeUserIds NOT LIKE '%"%')
        ),
        team_status_counts AS (
          SELECT 
            COUNT(CASE WHEN st.IsDraft = TRUE THEN 1 END) AS draft_count,
            COUNT(CASE WHEN st.IsTerminal = TRUE THEN 1 END) AS closed_count,
            COUNT(CASE WHEN st.IsDraft = FALSE AND st.IsTerminal = FALSE THEN 1 END) AS open_count
          FROM filteredItems fi
          JOIN status_templates st ON fi.StatusId = st.Id
          WHERE fi.AssigneeTeamId = '${teamId}'
        )
        SELECT 
          t.total,
          tuu.count AS user_unassigned_count,
          tsc.draft_count,
          tsc.closed_count,
          tsc.open_count
        FROM 
          team_items t, 
          team_user_unassigned tuu,
          team_status_counts tsc
      `;
      
      const result = await conn.query(query);
      const stats = result.toArray()[0].toJSON();
      
      const total = Number(stats.total) || 0;
      const userUnassignedCount = Number(stats.user_unassigned_count) || 0;
      const draftCount = Number(stats.draft_count) || 0;
      const closedCount = Number(stats.closed_count) || 0;
      const openCount = Number(stats.open_count) || 0;
      
      // Update team stats
      teamStats.value = {
        filteredCount: total
      };
      
      userUnassignedStats.value = {
        count: userUnassignedCount,
        total: total,
        percentage: total > 0 ? (userUnassignedCount / total) * 100 : 0
      };
      
      draftStats.value = {
        count: draftCount,
        total: total,
        percentage: total > 0 ? (draftCount / total) * 100 : 0
      };
      
      openStats.value = {
        count: openCount,
        total: total,
        percentage: total > 0 ? (openCount / total) * 100 : 0
      };
      
      closedStats.value = {
        count: closedCount,
        total: total,
        percentage: total > 0 ? (closedCount / total) * 100 : 0
      };
      
      // Clear unassigned stats in team view
      unassignedStats.value = { count: 0, total: 0, percentage: 0 };
    }
  } catch (error) {
    console.error("Error executing stats queries:", error);
    // Reset all stats on error
    resetStats();
  }
};

const resetStats = () => {
  unassignedStats.value = { count: 0, total: 0, percentage: 0 };
  userUnassignedStats.value = { count: 0, total: 0, percentage: 0 };
  draftStats.value = { count: 0, total: 0, percentage: 0 };
  openStats.value = { count: 0, total: 0, percentage: 0 };
  closedStats.value = { count: 0, total: 0, percentage: 0 };
  teamStats.value = { filteredCount: 0 };
};

// Debounce computeStats to avoid too many queries
const recomputeStats = debounce(() => {
  computeStats();
}, 100);

// Watch for changes in filter context
watch(
  () => filterContext.value.filtersLastUpdated,
  () => {
    recomputeStats();
  },
  { deep: true }
);

// Watch for changes in selected team
watch(
  () => selectedTeam.value,
  () => {
    recomputeStats();
  }
);

const isMounted = ref(false);
onMounted(async () => {
  //await store.commit('resetFilters');
  isMounted.value = true;
  // Directly compute stats on mount if data is already loaded
  if (dataLoaded.value) {
    await computeStats();
  }
});
</script>

<style scoped>
.v-chip {
  padding: 0 4px !important;
}
.v-data-table .v-icon {
  opacity: 0.6;
}

.chart {
  height: 400px;
}
.v-list-group__items .v-list-item {
  padding-inline-start: 16px !important;
}

.left-column, .right-column {
  height: calc(100vh - 64px);
  overflow-y: auto;
}

.client-id-cell {
  min-width: 200px;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>